import { Image, Palette, Video } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import type { StepProps } from "./types"

export function StepBranding({ data, onChange }: StepProps) {
  const t = useTranslation()
  const { webinarTheme, webinarLogo, webinarBackground, webinarIntroVideo } = data

  return (
    <div className="space-y-5">
      {/* Theme Colors */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-4">
        <div className="flex items-center gap-2 mb-0.5">
          <Palette className="w-4 h-4 text-primary" />
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.webinarTheme")}</p>
        </div>
        <p className="text-xs text-slate-500 mt-2">{t("webinar.customizeColorScheme")}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="primaryColor" className="font-medium mb-2 block text-sm">{t("webinar.primaryColor")}</Label>
            <div className="flex items-center gap-3">
              <div className="relative h-11 w-11 shrink-0">
                <input
                  type="color"
                  id="primaryColor"
                  value={webinarTheme.primary}
                  onChange={(e) => onChange({ webinarTheme: { ...webinarTheme, primary: e.target.value } })}
                  className="absolute inset-0 w-full h-full rounded-lg border border-stroke-100 dark:border-border dark:border-border cursor-pointer opacity-0"
                />
                <div
                  className="h-11 w-11 rounded-lg border-2 border-stroke-100 dark:border-border dark:border-border cursor-pointer"
                  style={{ backgroundColor: webinarTheme.primary }}
                />
              </div>
              <Input
                value={webinarTheme.primary}
                onChange={(e) => onChange({ webinarTheme: { ...webinarTheme, primary: e.target.value } })}
                placeholder="#042531"
                className="h-11 font-mono"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="navbarColor" className="font-medium mb-2 block text-sm">{t("webinar.navbarColor")}</Label>
            <div className="flex items-center gap-3">
              <div className="relative h-11 w-11 shrink-0">
                <input
                  type="color"
                  id="navbarColor"
                  value={webinarTheme.navBar}
                  onChange={(e) => onChange({ webinarTheme: { ...webinarTheme, navBar: e.target.value } })}
                  className="absolute inset-0 w-full h-full rounded-lg border border-stroke-100 dark:border-border dark:border-border cursor-pointer opacity-0"
                />
                <div
                  className="h-11 w-11 rounded-lg border-2 border-stroke-100 dark:border-border dark:border-border cursor-pointer"
                  style={{ backgroundColor: webinarTheme.navBar }}
                />
              </div>
              <Input
                value={webinarTheme.navBar}
                onChange={(e) => onChange({ webinarTheme: { ...webinarTheme, navBar: e.target.value } })}
                placeholder="#043040"
                className="h-11 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Preview strip */}
        <div className="rounded-xl overflow-hidden border border-stroke-100 dark:border-border dark:border-border mt-2">
          <div className="h-10 px-4 flex items-center gap-2" style={{ backgroundColor: webinarTheme.navBar }}>
            <div className="h-5 w-5 rounded-full bg-card/20" />
            <div className="h-2 w-24 rounded bg-card/30" />
          </div>
          <div className="h-16 flex items-center justify-center" style={{ backgroundColor: webinarTheme.primary }}>
            <span className="text-white/80 text-xs">{t("webinar.themePreview")}</span>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-primary" />
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.webinarLogo")}</p>
        </div>
        <p className="text-xs text-slate-500">{t("webinar.displayedInHeader")}</p>
        <Input
          value={webinarLogo}
          onChange={(e) => onChange({ webinarLogo: e.target.value })}
          placeholder="https://your-cdn.com/logo.png"
          className="h-11"
        />
        {webinarLogo && (
          <div className="rounded-lg border border-stroke-100 dark:border-border dark:border-border p-3 bg-slate-50 dark:bg-card/40">
            <img src={webinarLogo} alt="Logo preview" className="h-12 max-w-[160px] object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
          </div>
        )}
      </div>

      {/* Background */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-primary" />
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.backgroundImage")}</p>
        </div>
        <p className="text-xs text-slate-500">{t("webinar.backdropShown")}</p>
        <Input
          value={webinarBackground}
          onChange={(e) => onChange({ webinarBackground: e.target.value })}
          placeholder="https://your-cdn.com/background.jpg"
          className="h-11"
        />
        {webinarBackground && (
          <div className="rounded-lg border border-stroke-100 dark:border-border dark:border-border overflow-hidden h-28 bg-slate-50 dark:bg-card/40">
            <img src={webinarBackground} alt="Background preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
          </div>
        )}
      </div>

      {/* Intro Video */}
      <div className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Video className="w-4 h-4 text-primary" />
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.introVideo")}</p>
        </div>
        <p className="text-xs text-slate-500">{t("webinar.playedInWaitingRoom")}</p>
        <Input
          value={webinarIntroVideo}
          onChange={(e) => onChange({ webinarIntroVideo: e.target.value })}
          placeholder="https://your-cdn.com/intro.mp4"
          className="h-11"
        />
      </div>
    </div>
  )
}
